import { useState } from "react";
import { Plus } from "lucide-react";
import { useLuts, useSaveLut, type Lut } from "@/api/settings";
import { LutForm } from "@/components/forms/LutForm";
import { RequestError } from "@/components/RequestError";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { formatDate } from "@/lib/format";

type Editing = { mode: "new" } | { mode: "edit"; lut: Lut } | null;

export function LutPanel() {
  const [editing, setEditing] = useState<Editing>(null);
  const luts = useLuts();
  const save = useSaveLut();

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="max-w-2xl text-sm text-muted-foreground">
          A Letter of Undertaking lets a GST-registered company export services without paying
          IGST. File one for each financial year before issuing export invoices under LUT.
        </p>
        {editing === null && (
          <Button size="sm" onClick={() => setEditing({ mode: "new" })}>
            <Plus className="h-4 w-4" aria-hidden />
            Add LUT
          </Button>
        )}
      </div>

      {editing && (
        <Card>
          <CardHeader>
            <CardTitle>
              {editing.mode === "new" ? "New LUT" : `Edit LUT for FY ${editing.lut.fy_code}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <LutForm
              key={editing.mode === "edit" ? `${editing.lut.id}-${editing.lut.version}` : "new"}
              initial={editing.mode === "edit" ? editing.lut : null}
              submitLabel={editing.mode === "new" ? "Add LUT" : "Save LUT"}
              onSubmit={async (input) => {
                await save.mutateAsync({
                  input,
                  existing:
                    editing.mode === "edit"
                      ? { id: editing.lut.id, version: editing.lut.version }
                      : undefined,
                });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              onReload={() => {
                void luts.refetch();
                setEditing(null);
              }}
            />
          </CardContent>
        </Card>
      )}

      {luts.isPending ? (
        <p className="text-muted-foreground">Loading LUT registrations…</p>
      ) : luts.isError ? (
        <RequestError error={luts.error} />
      ) : luts.data.length === 0 ? (
        <EmptyState
          title="No LUT recorded"
          description="Only needed when the company is GST-registered and invoices exports under LUT."
        />
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {luts.data.map((lut) => (
            <li key={lut.id}>
              <Card className="h-full">
                <CardContent className="flex flex-col gap-1">
                  <span className="font-semibold">FY {lut.fy_code}</span>
                  <span className="break-all text-sm">ARN {lut.arn}</span>
                  <span className="text-xs text-muted-foreground">
                    Filed {formatDate(lut.filed_on)} · valid {formatDate(lut.valid_from)} to{" "}
                    {formatDate(lut.valid_to)}
                  </span>
                  <div className="pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditing({ mode: "edit", lut })}
                    >
                      Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
